import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { Tag, TagProps, Tooltip } from 'antd';
import clsx from 'clsx';
import {
  DynamicOrStaticProperty,
  resolveDynamicOrStaticProperty
} from 'lib/utils/dynamic-or-static-property';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { sleep } from 'lib/utils/sleep';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

export enum ActionState {
  NEUTRAL = 'NEUTRAL',
  PROCESSING = 'PROCESSING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS'
}

/**
 * A subset of the ActionState enum that reflects states that are not errors
 */
type ActionState_NotError =
  | ActionState.NEUTRAL
  | ActionState.PROCESSING
  | ActionState.SUCCESS;

/**
 * The state of an action tag
 */
type ActionTagState =
  | {
      state: ActionState_NotError;
      error: null;
    }
  | { state: ActionState.ERROR; error: Error };

/**
 * Properties that are passed a the custom render function for custom tag-like components
 */
export interface ActionTagRenderProps<T extends HasId> {
  record: T;
  onClick: ActionTagSettings<T>['onClick'];
  name: string;
  disabled: boolean;
  tagStyles: Pick<TagProps, 'icon' | 'color'>;
  actionState: ActionState;
}

/**
 * Handler definition for when an action tag is clicked
 */
export type ActionTagOnClick<
  T extends HasId,
  Extra extends Record<string, any>
> = (record: T, extra?: Extra) => Promise<void | any> | void | any;

/**
 * A clickable action in a User table
 */
export interface ActionTagSettings<T extends HasId> {
  /**
   * If the action should be displayed.
   *
   * Defaults to `true`
   */
  shouldShow?: boolean | ((a: T) => boolean);
  /**
   * The display name of the action
   */
  name: DynamicOrStaticProperty<string, T>;
  /**
   * The on-click event for the action
   */
  onClick: ActionTagOnClick<T, any>;
  toolTip?: DynamicOrStaticProperty<string, T>;
  initialState?: (a: T) => ActionState_NotError;
  /**
   * An optional custom component render for the ActionTag
   * @param props
   * @returns
   */
  render?: (props: ActionTagRenderProps<T>) => ReactElement;

  /**
   * Modify how the tag behaves after the configured action is successfully completed
   */
  onSuccessSettings?:
    | {
        /**
         * Block the user from triggering the action component again after a successful run
         *
         * If `initialState` returns a status other than success then the action can be triggered again after reloading the page
         */
        blockOnDone: true;
      }
    | {
        /**
         * Allow the user to triggering the action component again after a successful run
         */
        blockOnDone: false;
        /**
         * The time in milliseconds before the action component returns to a default state
         */
        timeout: number;
      };
  /**
   * Callback for when the action completes successfully
   * @param refetchTable
   * @returns
   */
  onSuccess?: (refetchTable: () => void) => void;
  /**
   * Callback for when the action fails to complete
   * @param error
   * @param metadata
   * @returns
   */
  onError?: (error: Error, metadata: { action: string; id: string }) => void;
}

type HasId = { id: string };

export interface ActionTagProps<T extends HasId> {
  record: T;
  action: ActionTagSettings<T>;
}

export function ActionTag<T extends HasId>(props: ActionTagProps<T>) {
  // Track the state of the tag
  const [state, setState] = useState<ActionTagState>({
    state: ActionState.NEUTRAL,
    error: null
  });

  // Update the state when the record changes
  useEffect(
    () => {
      if (props.action.initialState) {
        setCurrentState(props.action.initialState(props.record));
      }
    },
    // eslint-disable-next-line
    [props.record]
  );

  function setCurrentState(newState: ActionState_NotError) {
    setState(s => ({ ...s, state: newState, error: null }));
  }

  const isDisabled = [ActionState.SUCCESS, ActionState.PROCESSING].includes(
    state.state
  );

  const tooltipText = useMemo(
    () => resolveDynamicOrStaticProperty(props.action.toolTip, props.record),
    [props.action.toolTip, props.record]
  );

  const name = useMemo(
    () => resolveDynamicOrStaticProperty(props.action.name, props.record),
    [props.action.name, props.record]
  );

  const doAction = useCallback(
    async (recordOverride?: T, extra?: Record<string, any>) => {
      if (isDisabled) return;
      try {
        setCurrentState(ActionState.PROCESSING);
        await props.action.onClick(recordOverride ?? props.record, extra);
        setCurrentState(ActionState.SUCCESS);

        // Check if the action tag should block after a successful run
        if (props.action.onSuccessSettings?.blockOnDone) return;

        /**
         * Otherwise show a success message for `timeout` seconds
         */
        const timeout = props.action.onSuccessSettings?.timeout ?? 2000;

        // Wait `timeout` seconds
        await sleep(timeout);

        // Reset the state
        setCurrentState(ActionState.NEUTRAL);
        props.action.onSuccess?.(() => !!0);
      } catch (e) {
        const error = resolveError(e);
        // Save the error state
        setState(s => ({ ...s, error, state: ActionState.ERROR }));

        // Forward the error if an error listener is available
        if (props.action.onError) {
          props.action.onError?.(error, {
            action: name,
            id: props.record.id
          });
        } else {
          console.warn(
            `WARNING: An error for action ${name} on row ${props.record.id} was not handled.`
          );
          console.error(error);
        }
      }
    },
    [props.action, props.record, isDisabled, name]
  );

  const emptyDoAction = useCallback(() => doAction(), [doAction]);

  const tagStyles = useTagStyles(state.state);

  return (
    <Tooltip title={tooltipText}>
      {props.action.render ? (
        <props.action.render
          record={props.record}
          onClick={doAction}
          name={name}
          disabled={isDisabled}
          tagStyles={tagStyles}
          actionState={state.state}
        />
      ) : (
        <Tag
          {...tagStyles}
          className={clsx('clickable', isDisabled ? 'disabled' : '')}
          style={{ padding: '0.1rem', width: '100%' }}
          onClick={emptyDoAction}
        >
          {name}
        </Tag>
      )}
    </Tooltip>
  );
}

/**
 * Hook to return the styles for the action tag based on its current state.
 */
export function useTagStyles(actionState: ActionState) {
  return useMemo<TagProps>(() => {
    switch (actionState) {
      case ActionState.NEUTRAL:
        return { color: 'default' };
      case ActionState.PROCESSING:
        return { icon: <SyncOutlined spin />, color: 'gray' };
      case ActionState.ERROR:
        return { icon: <CloseCircleOutlined />, color: 'error' };
      case ActionState.SUCCESS:
        return { icon: <CheckCircleOutlined />, color: 'success' };
      default:
        return {};
    }
  }, [actionState]);
}
